import { useState } from "react";

import { useNavigate } from "@tanstack/react-router";

import { VERSION_AVAILABILITIES } from "@nebulize/sdk";

import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Page,
  Panel,
  Select,
  Skeleton,
  Stack,
  StatusBadge,
  type StatusTone,
} from "@/components/ui";

import { PagedTable } from "../components/PagedTable";
import {
  useCatalog,
  useRuleAvailability,
  useVersions,
  type ActivitySummary,
  type PublishedVersion,
  type VersionAvailability,
} from "../lib/queries";

const TONE: Record<VersionAvailability, StatusTone> = {
  published: "success",
  deprecated: "warning",
  withdrawn: "danger",
  revoked: "danger",
};

function availabilityOf(row: { availability?: VersionAvailability }): VersionAvailability {
  return row.availability ?? "published";
}

function AvailabilityCell({ row }: { readonly row: { availability?: VersionAvailability } }) {
  const value = availabilityOf(row);
  return <StatusBadge tone={TONE[value]}>{value}</StatusBadge>;
}

/** Every activity, then every version of the one opened, and the ruling that retires it. */
export function Versions() {
  const navigate = useNavigate();
  const [activityId, setActivityId] = useState<string | undefined>(undefined);
  const [ruling, setRuling] = useState<PublishedVersion | null>(null);
  const [availability, setAvailability] = useState<VersionAvailability>("deprecated");
  const [reason, setReason] = useState("");
  const catalog = useCatalog();
  const versions = useVersions(activityId);
  const rule = useRuleAvailability();

  const publish = (
    <Button
      variant="primary"
      onClick={() => void navigate({ to: "/publisher/validation" })}
    >
      Publish a version
    </Button>
  );

  if (catalog.isPending) {
    return (
      <Page title="Versions" eyebrow="Publisher" actions={publish}>
        <Skeleton height={220} />
      </Page>
    );
  }

  if (catalog.isError) {
    return (
      <Page title="Versions" eyebrow="Publisher" actions={publish}>
        <ErrorState
          headline="Catalog unavailable"
          onRetry={() => void catalog.refetch()}
          retryLabel="Retry"
        />
      </Page>
    );
  }

  const activities = catalog.data.activities ?? [];

  return (
    <Page title="Versions" eyebrow="Publisher" actions={publish}>
      <Panel title="Activities">
        <PagedTable
          rows={activities}
          caption="Activities"
          density="comfortable"
          emptyHeadline="Nothing published"
          placeholder="Find an activity"
          match={(row, needle) =>
            `${row.name} ${row.activity_id} ${row.version}`.toLowerCase().includes(needle)
          }
          columns={[
            { key: "name", header: "Activity", emphasis: true },
            { key: "activity_id", header: "Id", mono: true },
            { key: "version", header: "Newest", mono: true, width: "110px" },
            {
              key: "availability",
              header: "Availability",
              render: (row: ActivitySummary) => <AvailabilityCell row={row} />,
            },
            {
              key: "is_bundle",
              header: "Bundle",
              muted: true,
              render: (row: ActivitySummary) => (row.is_bundle === true ? "bundle" : "activity"),
            },
            {
              key: "open",
              header: "",
              align: "right",
              width: "140px",
              render: (row: ActivitySummary) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setActivityId(row.activity_id);
                  }}
                >
                  Versions
                </Button>
              ),
            },
          ]}
        />
      </Panel>

      <Panel title="Versions" subtitle={activityId}>
        {activityId === undefined ? (
          <EmptyState bare headline="No activity opened" />
        ) : versions.isPending ? (
          <Skeleton height={160} />
        ) : versions.isError ? (
          <ErrorState
            headline="Versions unavailable"
            onRetry={() => void versions.refetch()}
            retryLabel="Retry"
          />
        ) : (
          <Stack gap={4}>
            <PagedTable
              rows={versions.data}
              caption="Published versions"
              density="comfortable"
              emptyHeadline="No versions"
              placeholder="Find a version"
              match={(row, needle) =>
                `${row.version} ${availabilityOf(row)}`.toLowerCase().includes(needle)
              }
              columns={[
                { key: "version", header: "Version", emphasis: true, mono: true },
                {
                  key: "availability",
                  header: "Availability",
                  render: (row: PublishedVersion) => <AvailabilityCell row={row} />,
                },
                {
                  key: "digest",
                  header: "Digest",
                  mono: true,
                  muted: true,
                  render: (row: PublishedVersion) => row.digest.digest ?? "-",
                },
                {
                  key: "signature",
                  header: "Signed",
                  muted: true,
                  render: (row: PublishedVersion) => row.signature?.key_id ?? "-",
                },
                {
                  key: "superseded_by",
                  header: "Superseded by",
                  mono: true,
                  muted: true,
                  render: (row: PublishedVersion) => row.superseded_by ?? "-",
                },
                {
                  key: "rule",
                  header: "",
                  align: "right",
                  width: "110px",
                  render: (row: PublishedVersion) => (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setRuling(row);
                      }}
                    >
                      Rule
                    </Button>
                  ),
                },
              ]}
            />
            {rule.isError ? <ErrorState headline={rule.error.message} /> : null}
          </Stack>
        )}
      </Panel>

      <ConfirmDialog
        open={ruling !== null}
        title="Rule on a version"
        description={ruling?.version}
        confirmLabel="Rule"
        loading={rule.isPending}
        onConfirm={() => {
          if (ruling === null) return;
          rule.mutate({
            activityId: ruling.activity_id,
            version: ruling.version,
            availability,
            reason,
          });
          setRuling(null);
        }}
        onCancel={() => {
          setRuling(null);
        }}
      >
        <Stack gap={4}>
          <Field label="Availability">
            <Select
              value={availability}
              options={VERSION_AVAILABILITIES.map((one) => ({ value: one, label: one }))}
              onChange={(event) => {
                setAvailability(event.target.value as VersionAvailability);
              }}
            />
          </Field>
          <Field label="Reason">
            <Input
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </Field>
        </Stack>
      </ConfirmDialog>
    </Page>
  );
}
